import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import DOMPurify from 'dompurify';
import {
  ArrowLeft,
  BookOpen,
  Share2,
  Link as LinkIcon,
  Heart,
  ArrowUp,
  Bookmark,
  Eye,
  EyeOff,
  Sparkles,
} from 'lucide-react';
import { api } from '../../../shared/api/api.js';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';
import { useModalStore } from '../../../shared/store/useModalStore.js';
import {
  type HeadingItem,
  calculateReadingTime,
  extractHeadingsFromElement,
  handleShareArticle,
} from './blog-post/blogPostUtils.js';
import { BlogHeroHeader } from './blog-post/BlogHeroHeader.js';
import { BlogTableOfContents } from './blog-post/BlogTableOfContents.js';
import { BlogAuthorBio } from './blog-post/BlogAuthorBio.js';
import { BlogRelatedBooks } from './blog-post/BlogRelatedBooks.js';
import { BlogRelatedArticles } from './blog-post/BlogRelatedArticles.js';
import {
  ReadingControlsBar,
  type FontFamilyOption,
  type FontSizeOption,
  type LineSpacingOption,
  type ParaSpacingOption,
  type ReaderThemeOption,
} from './blog-post/ReadingControlsBar.js';
import { AskAuthorModal } from './blog-post/AskAuthorModal.js';
import { useBookmarks } from '../hooks/useBookmarks.js';
import { SeoHead, createBlogPostingSchema } from '../../../shared/components/index.js';

interface BlogPostViewProps {
  slug?: string;
  onBack?: () => void;
}

export const BlogPostView: React.FC<BlogPostViewProps> = ({ slug, onBack }) => {
  const queryClient = useQueryClient();
  const { showToast } = useNotificationStore();
  const { setViewingBook } = useModalStore();
  const { isBookmarked, toggleBookmark } = useBookmarks();

  // Use localStorage to persist the "liked" status for this specific post
  const [isLiked, setIsLiked] = useState(() => {
    if (typeof window !== 'undefined' && slug) {
      return localStorage.getItem(`uibn_blog_liked_${slug}`) === 'true';
    }
    return false;
  });

  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: () => api.getPostBySlug(slug || ''),
    enabled: !!slug,
  });

  // Query to fetch books matching the category for the Related Books Showcase
  const { data: booksData } = useQuery({
    queryKey: ['related-books-blog', post?.category],
    queryFn: () =>
      api.getBooks({
        category: post?.category && post.category !== 'Umum' ? post.category : undefined,
        scope: 'public',
      }),
    enabled: !!post?.category,
  });

  const likeMutation = useMutation({
    mutationFn: (id: string) => api.likePost(id),
    onSuccess: () => {
      setIsLiked(true);
      if (slug) {
        localStorage.setItem(`uibn_blog_liked_${slug}`, 'true');
      }
      queryClient.invalidateQueries({ queryKey: ['blog-post', slug] });
      showToast('Apresiasi Berhasil', 'Terima kasih atas apresiasi Anda!', 'success');
    },
  });

  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [headings, setHeadings] = useState<HeadingItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [isZenMode, setIsZenMode] = useState(false);

  // Reader Font Family & Font Size Customization States
  const [fontFamily, setFontFamily] = useState<FontFamilyOption>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('uibn_blog_font_family');
      if (saved === 'serif' || saved === 'mono' || saved === 'sans') return saved;
    }
    return 'sans';
  });

  const [fontSize, setFontSize] = useState<FontSizeOption>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('uibn_blog_font_size');
      if (saved === 'sm' || saved === 'base' || saved === 'lg' || saved === 'xl') return saved;
    }
    return 'base';
  });

  const [lineSpacing, setLineSpacing] = useState<LineSpacingOption>(() => {
    if (post?.lineSpacing) return post.lineSpacing;
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('uibn_blog_line_spacing');
      if (saved === 'tight' || saved === 'normal' || saved === 'relaxed' || saved === 'loose') return saved;
    }
    return 'normal';
  });

  const [paraSpacing, setParaSpacing] = useState<ParaSpacingOption>(() => {
    if (post?.paraSpacing) return post.paraSpacing;
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('uibn_blog_para_spacing');
      if (saved === 'compact' || saved === 'normal' || saved === 'relaxed' || saved === 'spacious') return saved;
    }
    return 'normal';
  });

  const [readerTheme, setReaderTheme] = useState<ReaderThemeOption>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('uibn_blog_reader_theme');
      if (saved === 'white' || saved === 'sepia' || saved === 'cream' || saved === 'dark' || saved === 'mint') return saved;
    }
    return 'white';
  });

  // Sync state if post prop changes or loads with specific author spacing settings
  useEffect(() => {
    if (post?.lineSpacing) {
      setLineSpacing(post.lineSpacing);
    }
    if (post?.paraSpacing) {
      setParaSpacing(post.paraSpacing);
    }
  }, [post?.id, post?.lineSpacing, post?.paraSpacing]);

  const handleFontFamilyChange = (font: FontFamilyOption) => {
    setFontFamily(font);
    if (typeof window !== 'undefined') {
      localStorage.setItem('uibn_blog_font_family', font);
    }
  };

  const handleFontSizeChange = (size: FontSizeOption) => {
    setFontSize(size);
    if (typeof window !== 'undefined') {
      localStorage.setItem('uibn_blog_font_size', size);
    }
  };

  const handleLineSpacingChange = (line: LineSpacingOption) => {
    setLineSpacing(line);
    if (typeof window !== 'undefined') {
      localStorage.setItem('uibn_blog_line_spacing', line);
    }
  };

  const handleParaSpacingChange = (para: ParaSpacingOption) => {
    setParaSpacing(para);
    if (typeof window !== 'undefined') {
      localStorage.setItem('uibn_blog_para_spacing', para);
    }
  };

  const handleReaderThemeChange = (theme: ReaderThemeOption) => {
    setReaderTheme(theme);
    if (typeof window !== 'undefined') {
      localStorage.setItem('uibn_blog_reader_theme', theme);
    }
  };

  const handleResetControls = () => {
    handleFontFamilyChange('sans');
    handleFontSizeChange('base');
    handleLineSpacingChange('normal');
    handleParaSpacingChange('normal');
    handleReaderThemeChange('white');
  };

  // Scroll listener for reading progress bar and floating top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

      const scrollHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight,
        document.documentElement.offsetHeight,
        document.body.offsetHeight
      );

      const clientHeight =
        window.innerHeight ||
        document.documentElement.clientHeight ||
        document.body.clientHeight ||
        0;
      const totalHeight = scrollHeight - clientHeight;

      if (totalHeight > 0) {
        const progress = Math.min(100, Math.max(0, (scrollTop / totalHeight) * 100));
        setScrollProgress(progress);
      } else {
        setScrollProgress(0);
      }

      setShowScrollTop(scrollTop > 150);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleHeadingClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      setActiveId(id);
      window.history.pushState(null, '', `#${id}`);
    }
  };

  // Extract headings from DOM content of post for dynamic Table of Contents
  useEffect(() => {
    if (!post || !post.content) return;

    const timer = setTimeout(() => {
      const articleEl = document.querySelector('.blog-prose');
      if (!articleEl) return;
      const extracted = extractHeadingsFromElement(articleEl);
      setHeadings(extracted);
    }, 350);

    return () => clearTimeout(timer);
  }, [post]);

  // ScrollSpy with IntersectionObserver to highlight currently active section
  useEffect(() => {
    if (headings.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -55% 0px',
      threshold: 0,
    };

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      const visibleEntries = entries.filter((entry) => entry.isIntersecting);
      if (visibleEntries.length > 0) {
        setActiveId(visibleEntries[0].target.id);
      }
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    headings.forEach((heading) => {
      const el = document.getElementById(heading.id);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, [headings]);

  const onShare = () => {
    handleShareArticle(
      post?.title,
      post?.summary || post?.subtitle || 'Baca artikel menarik ini di Blog UIBN',
      showToast
    );
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 animate-pulse">
        <div className="h-4 bg-slate-100 rounded w-24 mb-8" />
        <div className="h-12 bg-slate-100 rounded w-3/4 mb-4" />
        <div className="h-4 bg-slate-100 rounded w-1/4 mb-12" />
        <div className="aspect-video bg-slate-100 rounded-3xl mb-12" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-4 bg-slate-100 rounded w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="max-w-xl mx-auto px-4 py-32 text-center">
        <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-6 text-rose-500">
          <BookOpen className="w-10 h-10" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Artikel Tidak Ditemukan</h2>
        <p className="text-slate-600 mb-8">Maaf, artikel yang Anda cari tidak tersedia atau telah dihapus.</p>
        <a
          href="/blog"
          onClick={(e) => {
            e.preventDefault();
            if (onBack) {
              onBack();
            }
          }}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-5 h-5" />
          Kembali ke Blog
        </a>
      </div>
    );
  }

  // Sanitize content
  const sanitizedContent = DOMPurify.sanitize(post.content);
  const readingTime = calculateReadingTime(post.content);

  const fontFamilyClass = `reader-font-${fontFamily}`;
  const fontSizeClass = `reader-size-${fontSize}`;

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://uibn.id';
  const postUrl = `${origin}/blog/${post.slug}`;
  const blogPostingSchema = createBlogPostingSchema({
    title: post.title,
    description: post.excerpt || post.summary || post.title,
    url: postUrl,
    imageUrl: post.coverUrl || post.coverImage || undefined,
    datePublished: post.createdAt,
    dateModified: post.updatedAt || post.createdAt,
    authorName: post.authorName || 'Tim Redaksi UIBN',
    category: post.category || undefined,
    keywords: post.tags || undefined,
  }, origin);

  return (
    <article className={`min-h-screen transition-colors duration-300 reader-theme-${readerTheme} relative`}>
      <SeoHead
        title={post.title}
        description={post.excerpt || post.summary || post.title}
        canonicalUrl={postUrl}
        ogType="article"
        ogImage={post.coverUrl || post.coverImage || undefined}
        author={post.authorName || 'Tim Redaksi UIBN'}
        publishedTime={post.createdAt}
        modifiedTime={post.updatedAt || post.createdAt}
        keywords={post.tags || [post.category || 'literasi', 'UIBN blog', 'penerbitan']}
        structuredData={blogPostingSchema}
      />

      {/* Zen Mode Floating Exit Button */}
      {isZenMode && (
        <button
          type="button"
          onClick={() => setIsZenMode(false)}
          className="fixed top-6 right-6 z-[9999] px-4 py-2.5 bg-slate-900/95 hover:bg-black text-white hover:text-amber-400 font-extrabold text-xs rounded-2xl shadow-2xl flex items-center gap-2 cursor-pointer transition-all border border-slate-800/80 hover:scale-105 active:scale-95"
          title="Keluar dari Mode Zen (Kembalikan Navigasi)"
        >
          <EyeOff className="w-4 h-4 text-amber-500 animate-pulse" />
          <span>Keluar Mode Zen</span>
        </button>
      )}

      {/* Hero Header & Structured Data (Hidden in Zen Mode) */}
      {!isZenMode && (
        <BlogHeroHeader
          post={post}
          readingTime={readingTime}
          scrollProgress={scrollProgress}
          onBack={onBack}
          onShare={onShare}
          isBookmarked={post?.slug ? isBookmarked(post.slug) : false}
          onToggleBookmark={() => post?.slug && toggleBookmark(post.slug, post.title)}
        />
      )}

      {/* Main Content & Sidebar Grid */}
      <div className={`w-full pb-24 text-left animate-fade-in min-h-screen transition-all duration-300 ${
        isZenMode 
          ? 'max-w-4xl 2xl:max-w-5xl mx-auto px-6 pt-16 sm:pt-24' 
          : 'px-4 sm:px-8 lg:px-12 2xl:px-16 lg:grid lg:grid-cols-12 lg:gap-12 2xl:gap-16'
      }`}>
        {/* Sidebar Table of Contents (Desktop - Hidden in Zen Mode) */}
        {!isZenMode && (
          <BlogTableOfContents
            headings={headings}
            activeId={activeId}
            onHeadingClick={handleHeadingClick}
            variant="desktop"
          />
        )}

        {/* Main Content Area */}
        <div className={isZenMode ? 'w-full' : 'lg:col-span-9 2xl:col-span-9 w-full max-w-5xl'}>
          {/* Mobile/Tablet Collapsible Table of Contents (Hidden in Zen Mode) */}
          {!isZenMode && (
            <BlogTableOfContents
              headings={headings}
              activeId={activeId}
              onHeadingClick={handleHeadingClick}
              variant="mobile"
            />
          )}

          {/* Reader Comfort & Font Family Controls Bar */}
          <ReadingControlsBar
            fontFamily={fontFamily}
            fontSize={fontSize}
            lineSpacing={lineSpacing}
            paraSpacing={paraSpacing}
            readerTheme={readerTheme}
            onChangeFontFamily={handleFontFamilyChange}
            onChangeFontSize={handleFontSizeChange}
            onChangeLineSpacing={handleLineSpacingChange}
            onChangeParaSpacing={handleParaSpacingChange}
            onChangeReaderTheme={handleReaderThemeChange}
            onResetControls={handleResetControls}
            isZenMode={isZenMode}
            onChangeZenMode={setIsZenMode}
          />

          {isZenMode && (
            <div className="mb-8 p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 flex items-center justify-between text-xs text-amber-900 animate-fade-in">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
                <span className="font-bold">Mode Zen Aktif:</span>
                <span className="font-medium text-slate-500">Semua gangguan disembunyikan. Klik pojok kanan atas atau tombol di atas untuk keluar.</span>
              </div>
            </div>
          )}

          {/* Article Title when in Zen Mode (since HeroHeader is hidden) */}
          {isZenMode && (
            <header className="mb-12 border-b border-slate-100 pb-8">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight mb-4">
                {post.title}
              </h1>
              {post.subtitle && (
                <p className="text-lg text-slate-500 italic font-serif">
                  "{post.subtitle}"
                </p>
              )}
              <div className="flex items-center gap-2 text-xs text-slate-400 font-bold uppercase tracking-widest mt-4">
                <span>Oleh {post.authorName}</span>
                <span>•</span>
                <span>{readingTime} Menit Bacaan</span>
              </div>
            </header>
          )}

          {/* Article Content with Custom Typography & Expanded Unboxed Reading Area */}
          <div className={`blog-dropcap blog-prose max-w-none text-left tracking-normal transition-all duration-200 ${fontFamilyClass} ${fontSizeClass} line-spacing-${lineSpacing} para-spacing-${paraSpacing} reader-theme-${readerTheme}`}>
            <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
          </div>

          {/* Share & Like Action Bar (Hidden in Zen Mode) */}
          {!isZenMode && (
            <div className="mt-20 pt-12 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-8">
              <div className="flex items-center gap-4">
                <span className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">Bagikan:</span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={onShare}
                    className="w-11 h-11 rounded-2xl bg-slate-50 text-slate-600 flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-all border border-slate-100 hover:border-indigo-600 shadow-xs cursor-pointer"
                    title="Bagikan"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={onShare}
                    className="w-11 h-11 rounded-2xl bg-slate-50 text-slate-600 flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-all border border-slate-100 hover:border-indigo-600 shadow-xs cursor-pointer"
                    title="Salin Tautan"
                  >
                    <LinkIcon className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => post?.slug && toggleBookmark(post.slug, post.title)}
                    className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-all border shadow-xs cursor-pointer ${
                      post?.slug && isBookmarked(post.slug)
                        ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600'
                        : 'bg-slate-50 text-slate-600 border-slate-100 hover:bg-amber-500 hover:text-white hover:border-amber-500'
                    }`}
                    title={post?.slug && isBookmarked(post.slug) ? 'Hapus Bookmark' : 'Simpan ke Bookmark'}
                  >
                    <Bookmark className={`w-5 h-5 ${post?.slug && isBookmarked(post.slug) ? 'fill-current' : ''}`} />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 px-6 py-4 rounded-3xl bg-linear-to-r from-rose-50 to-rose-50/20 border border-rose-100/50">
                <div className="flex flex-col">
                  <span className="text-[10px] font-black text-rose-400 uppercase tracking-widest">
                    {post.likes || 0} Apresiasi
                  </span>
                  <span className="text-sm font-bold text-rose-900">Sukai wawasan ini?</span>
                </div>
                <button
                  type="button"
                  onClick={() => likeMutation.mutate(post.id)}
                  disabled={isLiked || likeMutation.isPending}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all group shadow-xs cursor-pointer ${
                    isLiked
                      ? 'bg-rose-500 text-white border-rose-500'
                      : 'bg-white border-rose-200 text-rose-500 hover:bg-rose-500 hover:text-white'
                  } border`}
                >
                  <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : 'group-hover:fill-current'}`} />
                  <span className="text-xs font-black uppercase tracking-wider">
                    {isLiked ? 'Sudah Diapresiasi' : 'Apresiasi'}
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Author Bio Card (Hidden in Zen Mode) */}
          {!isZenMode && (
            <BlogAuthorBio
              authorName={post.authorName}
              authorRole={post.authorRole}
              authorBio={post.authorBio}
              onOpenQuestionModal={() => setIsQuestionModalOpen(true)}
              onBack={onBack}
            />
          )}

          {/* Related Books Showcase (Hidden in Zen Mode) */}
          {!isZenMode && (
            <BlogRelatedBooks
              books={booksData?.books || []}
              category={post.category}
              onSelectBook={setViewingBook}
            />
          )}

          {/* Related Articles Showcase (Hidden in Zen Mode) */}
          {!isZenMode && (
            <BlogRelatedArticles
              currentPostId={post.id}
              currentCategory={post.category}
            />
          )}
        </div>
      </div>

      {/* Floating Back to Top Button with Smooth Transitions */}
      {typeof document !== 'undefined' && createPortal(
        <button
          type="button"
          onClick={scrollToTop}
          className={`fixed bottom-6 right-6 sm:bottom-10 sm:right-10 z-50 p-3.5 sm:p-4 rounded-full bg-indigo-600/90 hover:bg-indigo-600 backdrop-blur-md active:scale-95 text-white shadow-2xl hover:shadow-indigo-500/40 border border-white/20 flex items-center justify-center transition-all duration-500 ease-out cursor-pointer ${
            showScrollTop
              ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
              : 'opacity-0 translate-y-8 scale-75 pointer-events-none'
          }`}
          title="Kembali ke atas"
          aria-label="Kembali ke atas"
        >
          <ArrowUp className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>,
        document.body
      )}

      {/* Tanya Penulis Interactive Modal Portal */}
      <AskAuthorModal
        isOpen={isQuestionModalOpen}
        onClose={() => setIsQuestionModalOpen(false)}
        postId={post.id}
        postTitle={post.title}
        authorName={post.authorName}
      />
    </article>
  );
};
